import { useMemo, useState } from 'react';
import { Search, X } from 'lucide-react';
import {
  formatTime,
  searchEvents,
  shortDate,
  weekdayName,
  type CalendarEvent,
  type EventOccurrence,
  type SearchHit,
} from '@awwyeah/shared';
import { ownerNames, styleForOwners, type MemberView } from '../members';

/**
 * Instant search over every event, past and future (design Screen C · 8). Coming up
 * first, always; past results go quiet. No network: the whole calendar is already on the
 * phone.
 */
export function SearchOverlay({
  events,
  members,
  now,
  onClose,
  onOpenOccurrence,
}: {
  events: readonly CalendarEvent[];
  members: ReadonlyMap<string, MemberView>;
  now: Date;
  onClose: () => void;
  onOpenOccurrence: (occurrence: EventOccurrence) => void;
}): JSX.Element {
  const [query, setQuery] = useState('');
  const [scope, setScope] = useState<'all' | 'upcoming' | 'past'>('all');

  const results = useMemo(
    () => searchEvents(events, query, { now: now.getTime() }),
    [events, query, now],
  );

  const upcoming = scope === 'past' ? [] : results.upcoming;
  const past = scope === 'upcoming' ? [] : results.past;

  return (
    <div className="fixed inset-0 z-30 flex flex-col bg-bg pb-[env(safe-area-inset-bottom)] pt-[env(safe-area-inset-top)]">
      <div className="flex items-center gap-2 border-b border-line bg-surface px-4 py-3">
        <div className="flex h-12 flex-1 items-center gap-2 rounded-md border border-line bg-bg px-3">
          <Search className="h-4 w-4 shrink-0 text-fg-3" aria-hidden="true" />
          <input
            type="text"
            value={query}
            autoFocus
            data-testid="calendar-search-input"
            aria-label="Search events"
            placeholder="Search events"
            onChange={(event) => setQuery(event.target.value)}
            className="w-full bg-transparent text-base text-fg outline-none"
          />
          {query && (
            <button type="button" aria-label="Clear" onClick={() => setQuery('')}>
              <X className="h-4 w-4 text-fg-3" />
            </button>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          data-testid="calendar-search-close"
          className="h-12 px-2 text-sm font-semibold text-accent"
        >
          Done
        </button>
      </div>

      <div className="flex gap-2 px-4 py-2">
        {(['all', 'upcoming', 'past'] as const).map((option) => (
          <button
            key={option}
            type="button"
            onClick={() => setScope(option)}
            aria-pressed={scope === option}
            className={`h-9 rounded-full px-3 text-[13px] font-semibold capitalize ${
              scope === option
                ? 'bg-inverse text-inverse-fg'
                : 'border border-line bg-surface text-fg-2'
            }`}
          >
            {option === 'all' ? 'All time' : option}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto px-4 pb-24" data-testid="calendar-search-results">
        {query.trim().length === 0 && (
          <p className="py-8 text-sm text-fg-3">
            Searches titles, notes and locations — all of it on this phone, no network needed.
          </p>
        )}

        {query.trim().length > 0 && results.total === 0 && (
          <div className="flex flex-col gap-2 border-t-2 border-line-strong py-8">
            <p className="font-display text-xl">Nothing matches “{query.trim()}”</p>
            <p className="text-sm text-fg-2">Try a shorter word, or a place instead of a name.</p>
          </div>
        )}

        {upcoming.length > 0 && (
          <Group label={`Coming up · ${upcoming.length}`}>
            {upcoming.map((hit) => (
              <HitRow
                key={hit.event.id}
                hit={hit}
                members={members}
                now={now}
                onOpen={onOpenOccurrence}
              />
            ))}
          </Group>
        )}

        {past.length > 0 && (
          <Group label={`Earlier · ${past.length}`}>
            {past.map((hit) => (
              <HitRow
                key={hit.event.id}
                hit={hit}
                members={members}
                now={now}
                quiet
                onOpen={onOpenOccurrence}
              />
            ))}
          </Group>
        )}
      </div>
    </div>
  );
}

function Group({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <section className="pt-4">
      <h2 className="pb-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-fg-3">
        {label}
      </h2>
      <div className="flex flex-col gap-2">{children}</div>
    </section>
  );
}

function HitRow({
  hit,
  members,
  now,
  quiet = false,
  onOpen,
}: {
  hit: SearchHit;
  members: ReadonlyMap<string, MemberView>;
  now: Date;
  quiet?: boolean;
  onOpen: (occurrence: EventOccurrence) => void;
}): JSX.Element {
  const start = new Date(hit.occurrence.start);
  const names = ownerNames(hit.event.ownerIds, members);
  return (
    <button
      type="button"
      data-testid="event-item"
      data-event-id={hit.event.id}
      onClick={() => onOpen(hit.occurrence)}
      style={styleForOwners(hit.event.ownerIds, members).edge}
      className={`flex min-h-[56px] w-full items-center gap-3 rounded-sm border border-line border-l-4 bg-surface px-3 py-2 text-left ${quiet ? 'opacity-70' : ''}`}
    >
      <span className="min-w-0 flex-1">
        <span className={`block truncate font-semibold ${quiet ? 'text-sm' : 'text-base'}`}>
          {hit.event.title}
        </span>
        <span className="block truncate text-xs text-fg-3">
          {weekdayName(start).slice(0, 3)}, {shortDate(start, now)}
          {!hit.event.allDay && ` · ${formatTime(start)}`}
          {names && ` · ${names}`}
        </span>
      </span>
    </button>
  );
}
